import { useEffect, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import Card from 'components/card';
import { useDisclosure } from '@chakra-ui/hooks';
import SuccessModal from '../myOffer/components/SuccessModal';
import { MdPhone, MdEmail, MdCheckCircle, MdHome, MdKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/md';
import { FaMoneyBillWave, FaChartLine } from 'react-icons/fa';

const MyLeads = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [missionIdFilter, setMissionIdFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

    const toggleCard = (index: number) => {
        setExpandedCards((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const initUser = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/user/me`, config)
            .then((res) => {
                if (res.data && res.data.type !== 'seller') {
                    window.location.href = '/admin/my-request';
                }
            })
            .catch((err) => {
                console.log('No user info found', err);
            });
    };

    const initMyLeads = () => {
        const config = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + localStorage.getItem('token'),
            },
        };
        axios
            .get(`${CONFIG.apiUrl}/lead`, config)
            .then((res) => {
                setLeads(res.data);
                setFilteredLeads(res.data);
            })
            .catch((err) => {
                console.log('No user info found', err);
                window.location.href = '/auth/sign-in';
            });
    };

    // Filter leads by mission ID and status
    const filterLeads = () => {
        let filtered = leads;

        if (missionIdFilter) {
            filtered = filtered.filter(
                (lead) => lead.orderNumber === missionIdFilter
            );
        }

        if (statusFilter) {
            filtered = filtered.filter((lead) => lead.status === statusFilter);
        }

        setFilteredLeads(filtered);
    };

    // Handle filter input change
    const handleMissionIdFilterChange = (value: string) => {
        setMissionIdFilter(value);
    };

    const handleStatusFilterChange = (value: string) => {
        setStatusFilter(value);
    };

    // Clear filter
    const clearFilter = () => {
        setMissionIdFilter('');
        setStatusFilter('');
        setFilteredLeads(leads);
    };

    // Get unique mission IDs for selector
    const getUniqueMissionIds = () => {
        const missionIds = new Set();
        leads.forEach((lead) => {
            if (lead.orderNumber) {
                missionIds.add(lead.orderNumber);
            }
        });
        return Array.from(missionIds).sort();
    };

    // Get unique statuses for selector
    const getUniqueStatuses = () => {
        const statuses = new Set();
        leads.forEach((lead) => {
            if (lead.status) {
                statuses.add(lead.status);
            }
        });
        return Array.from(statuses).sort();
    };

    useEffect(() => {
        if (leads.length > 0) {
            filterLeads();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leads, missionIdFilter, statusFilter]);

    useEffect(() => {
        if (
            !localStorage.getItem('token') ||
            localStorage.getItem('token').length < 10
        ) {
            window.location.href = '/auth/sign-up';
        }

        const urlParams = new URLSearchParams(window.location.search);
        const created = urlParams.get('created');
        if (created === 'true' && !isOpen) {
            onOpen();
        }

        initMyLeads();
        initUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function timeAgo(date: any) {
        const now: any = new Date();
        const givenDate: any = new Date(date);
        const diffInSeconds = Math.floor((now - givenDate) / 1000);

        const days = Math.floor(diffInSeconds / (3600 * 24));
        const hours = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;

        if (days > 0) return `Il y a ${days} jour${days > 1 ? 's' : ''}`;
        if (hours > 0) return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
        if (minutes > 0)
            return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
        return `Il y a ${seconds} seconde${seconds > 1 ? 's' : ''}`;
    }

    const getPaymentStatus = (item: any) => {
        if (item.isPaid || item.paidAt) {
            return { label: 'Effectué', color: 'green', icon: true };
        } else if (item.status === 'pending' || item.approvalStatus === 'pending') {
            return { label: 'En attente', color: 'yellow', icon: false };
        } else if (item.status === 'rejected' || item.approvalStatus === 'rejected') {
            return { label: 'Refusé', color: 'red', icon: false };
        } else {
            return { label: 'En cours', color: 'blue', icon: false };
        }
    };

    const DiscoverCommandPage = () => (
        <Card extra="w-full pb-[10px] pt-[10px] px-[29px] h-fit shadow shadow-3xl rounded-[20px] border">
            <div className="mt-1">
                <p className="text-xl font-semibold text-navy-700 dark:text-white">
                    Vos leads envoyés 🎯
                </p>
                <p className="mt-2 text-base text-gray-600">
                    Consultez les leads que vous avez envoyés à vos clients.
                </p>
            </div>
        </Card>
    );

    const FilterSection = () => (
        <Card extra="w-full pb-4 pt-4 px-6 mt-5 shadow shadow-3xl rounded-[20px] border">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-navy-700 dark:text-white">
                            Mission :
                        </label>
                        <select
                            value={missionIdFilter}
                            onChange={(e) =>
                                handleMissionIdFilterChange(e.target.value)
                            }
                            className="focus:border-transparent rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Toutes les missions</option>
                            {getUniqueMissionIds().map((missionId: any) => (
                                <option key={missionId} value={missionId}>
                                    #{missionId}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-navy-700 dark:text-white">
                            Statut :
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) =>
                                handleStatusFilterChange(e.target.value)
                            }
                            className="focus:border-transparent rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Tous les statuts</option>
                            {getUniqueStatuses().map((status: any) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800">
                    {filteredLeads.length} résultat
                    {filteredLeads.length > 1 ? 's' : ''}
                </span>
            </div>
        </Card>
    );

    return (
        <div className="h-full w-full rounded-[20px]">
            <SuccessModal isOpen={isOpen} onClose={onClose} />
            <DiscoverCommandPage />
            <FilterSection />
            {filteredLeads.length === 0 ? (
                <div className="mt-10">
                    <p className="text-center text-lg text-gray-500">
                        {leads.length === 0
                            ? 'Aucun lead trouvé'
                            : 'Aucun lead ne correspond au filtre appliqué'}
                    </p>
                    {leads.length > 0 && (missionIdFilter || statusFilter) && (
                        <button
                            onClick={clearFilter}
                            className="mx-auto mt-3 block rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
                        >
                            Effacer le filtre
                        </button>
                    )}
                </div>
            ) : (
                <div className="mt-5 space-y-3">
                    {filteredLeads.map((item, index) => {
                        // Get contact info from body
                        const getFieldValue = (fieldName: string) => {
                            const field = item.body?.find(
                                (entry: any) => entry.field === fieldName
                            );
                            return field?.answer || 'N/A';
                        };

                        const firstName = getFieldValue('Prénom');
                        const lastName = getFieldValue('Nom');
                        const phone = getFieldValue('Téléphone');
                        const email = getFieldValue('Email');
                        const propertyType = getFieldValue('Type de bien');
                        const location = getFieldValue('Localisation') || getFieldValue('Département');
                        const isExpanded = expandedCards.has(index);
                        const paymentStatus = getPaymentStatus(item);

                        return (
                            <Card
                                key={index}
                                onClick={() => toggleCard(index)}
                                extra="w-full shadow-md rounded-[20px] border border-gray-200 overflow-hidden transition-all hover:cursor-pointer hover:shadow-lg"
                            >
                                {/* Compact Header - Always Visible */}
                                <div className="flex items-center justify-between gap-6 px-6 py-5">
                                    {/* Left: ID and Time */}
                                    <div className="flex items-start">
                                        <div className="min-w-[110px]">
                                            <h3 className="text-lg font-bold text-navy-700">
                                                #{item.orderNumber?.toString().substring(0, 6) || item.orderNumber}
                                            </h3>
                                            <p className="mt-1 text-xs text-gray-500">
                                                {timeAgo(item.createdAt)}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Main Content: Name, Phone, Email, Channels */}
                                    <div className="flex flex-1 flex-col gap-3">
                                        <h4 className="text-lg font-bold text-navy-700">
                                            {firstName} {lastName}
                                        </h4>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                                <MdPhone className="text-gray-500" size={18} />
                                                <span>{phone}</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                                <MdEmail className="text-blue-500" size={18} />
                                                <span>{email}</span>
                                            </div>
                                        </div>
                                        {item.channels && item.channels.length > 0 && (
                                            <div className="flex gap-2">
                                                {item.channels.map((channel: string, idx: number) => (
                                                    <span
                                                        key={idx}
                                                        className="rounded bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600"
                                                    >
                                                        {channel}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Center: Quality Score */}
                                    <div className="flex items-center">
                                        <div className="text-center">
                                            <div className="text-2xl font-bold text-navy-700">
                                                {item.leadrsRate || 0}
                                                {'/10'}
                                            </div>
                                            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                Qualité
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Price and Status */}
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-xl border-2 border-green-500 bg-green-50 px-5 py-3 text-center">
                                            <div className="mb-1 flex items-center justify-center gap-1.5 text-xs font-bold uppercase text-green-700">
                                                <FaMoneyBillWave size={11} />
                                                Lead vendu
                                            </div>
                                            <div className="text-2xl font-bold text-green-600">
                                                {item.leadPrice ? item.leadPrice.toFixed(2) : 'N/A'}€
                                            </div>
                                        </div>
                                        <div className={`rounded-xl border-2 px-5 py-3 text-center ${
                                            paymentStatus.color === 'green' ? 'border-green-500 bg-white' :
                                            paymentStatus.color === 'yellow' ? 'border-yellow-500 bg-white' :
                                            paymentStatus.color === 'red' ? 'border-red-500 bg-white' :
                                            'border-blue-500 bg-white'
                                        }`}>
                                            <div className={`mb-1 flex items-center justify-center gap-1.5 text-xs font-bold uppercase ${
                                                paymentStatus.color === 'green' ? 'text-green-700' :
                                                paymentStatus.color === 'yellow' ? 'text-yellow-700' :
                                                paymentStatus.color === 'red' ? 'text-red-700' :
                                                'text-blue-700'
                                            }`}>
                                                <span className="text-base">─</span>
                                                Virement
                                            </div>
                                            <div className={`flex items-center justify-center gap-1 text-base font-bold ${
                                                paymentStatus.color === 'green' ? 'text-green-600' :
                                                paymentStatus.color === 'yellow' ? 'text-yellow-600' :
                                                paymentStatus.color === 'red' ? 'text-red-600' :
                                                'text-blue-600'
                                            }`}>
                                                {paymentStatus.icon && <MdCheckCircle size={18} />}
                                                {paymentStatus.label}
                                            </div>
                                        </div>

                                        {/* Expand/Collapse Button */}
                                        <button
                                            className="ml-1 flex h-10 w-10 items-center justify-center transition-colors"
                                        >
                                            {isExpanded ? (
                                                <MdKeyboardArrowUp size={28} className="text-gray-400" />
                                            ) : (
                                                <MdKeyboardArrowDown size={28} className="text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {isExpanded && (
                                    <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                                        <div className="grid grid-cols-3 gap-6">
                                            {/* Transaction */}
                                            <div>
                                                <h5 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-gray-600">
                                                    <FaMoneyBillWave className="text-yellow-500" />
                                                    TRANSACTION
                                                </h5>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Prix de vente :</span>
                                                        <span className="font-semibold text-gray-800">
                                                            {item.leadPrice ? item.leadPrice.toFixed(2) : 'N/A'}€
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Virement :</span>
                                                        <span className={`font-semibold ${
                                                            paymentStatus.color === 'green' ? 'text-green-600' :
                                                            paymentStatus.color === 'yellow' ? 'text-yellow-600' :
                                                            paymentStatus.color === 'red' ? 'text-red-600' :
                                                            'text-blue-600'
                                                        }`}>
                                                            {paymentStatus.icon && '✓ '}{paymentStatus.label}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Date de vente :</span>
                                                        <span className="font-semibold text-gray-800">
                                                            {new Date(item.createdAt).toLocaleDateString('fr-FR')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Source */}
                                            <div>
                                                <h5 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-gray-600">
                                                    <FaChartLine className="text-blue-500" />
                                                    SOURCE
                                                </h5>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Source :</span>
                                                        <span className="font-semibold text-gray-800">Landing Page</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Date d'envoi :</span>
                                                        <span className="font-semibold text-gray-800">
                                                            {new Date(item.createdAt).toLocaleDateString('fr-FR')}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Informations */}
                                            <div>
                                                <h5 className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-gray-600">
                                                    <MdHome className="text-purple-500" />
                                                    INFORMATIONS
                                                </h5>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Propriétaire :</span>
                                                        <span className="font-semibold text-gray-800">Oui</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Type de bien :</span>
                                                        <span className="font-semibold text-gray-800">{propertyType}</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">Localisation :</span>
                                                        <span className="font-semibold text-gray-800">{location}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* <div className="mt-4">
                                            <button className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-purple-700">
                                                <FaFileInvoice />
                                                Télécharger la facture
                                            </button>
                                        </div> */}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MyLeads;
